import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  IoClose,
  IoTimeOutline,
  IoBusinessOutline,
  IoCalendarOutline,
  IoCartOutline,
} from "react-icons/io5";
import { format } from "date-fns";
import { ProductData, Schedule } from "../../../types/types";
import accessURL from "../../../../../../components/utils/access";
import ProductSelector from "./ProductSelector";
import TimeGrid from "./TimeGrid";
import { toast } from "react-toastify";

interface EditScheduleModalProps {
  schedule: Schedule | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const EditScheduleModal: React.FC<EditScheduleModalProps> = ({
  schedule,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<Partial<Schedule>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    startTime?: string;
    endTime?: string;
    objective?: string;
    products?: string;
  }>({});
  const [selectedProducts, setSelectedProducts] = useState<ProductData[]>([]);
  const [initialProductsLoaded, setInitialProductsLoaded] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialFormData, setInitialFormData] = useState<Partial<Schedule>>({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Load initial schedule data
  useEffect(() => {
    if (schedule) {
      const formatApiTime = (time: string) => {
        if (!time) return "";
        return time.substring(0, 5);
      };

      const formattedData = {
        id: schedule.id,
        customer: schedule.customer,
        startTime: formatApiTime(schedule.startTime),
        endTime: formatApiTime(schedule.endTime),
        dateOfVisit: format(new Date(schedule.dateOfVisit), "yyyy-MM-dd"),
        products: schedule.products,
        objective: schedule.objective,
      };

      setFormData(formattedData);
      setInitialFormData(formattedData);

      if (!initialProductsLoaded) {
        const productNames = JSON.parse(schedule.products || "[]");
        fetchInitialProducts(productNames);
      }
    }
  }, [schedule]);

  // Check for changes
  useEffect(() => {
    if (Object.keys(initialFormData).length > 0) {
      const hasObjectiveChanged =
        formData.objective !== initialFormData.objective;
      const hasStartTimeChanged =
        formData.startTime !== initialFormData.startTime;
      const hasEndTimeChanged = formData.endTime !== initialFormData.endTime;
      const hasProductsChanged =
        JSON.stringify(selectedProducts.map((p) => p.name)) !==
        initialFormData.products;

      const changes =
        hasObjectiveChanged ||
        hasStartTimeChanged ||
        hasEndTimeChanged ||
        hasProductsChanged;

      setHasChanges(changes);
    }
  }, [formData, selectedProducts, initialFormData]);

  const fetchInitialProducts = async (productNames: string[]) => {
    if (productNames.length === 0) return;

    try {
      const promises = productNames.map((name) =>
        accessURL.get(`/products?name=${encodeURIComponent(name)}&limit=1`)
      );

      const responses = await Promise.all(promises);
      const products = responses
        .map((response) => response.data.data[0])
        .filter((product) => product);

      setSelectedProducts(products);
      setInitialProductsLoaded(true);
    } catch (err) {
      console.error("Error fetching initial products:", err);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!formData.objective?.trim()) {
      newErrors.objective = "Objective is required";
    }
    if (!formData.startTime) {
      newErrors.startTime = "Start time is required";
    }
    if (!formData.endTime) {
      newErrors.endTime = "End time is required";
    }
    if (selectedProducts.length === 0) {
      newErrors.products = "At least one product must be selected";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const clearError = (field: keyof typeof errors) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!schedule?.id || !hasChanges) return;

    if (!validateForm()) {
      toast.warn("your form is not properly filled");
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleConfirmedSubmit = async () => {
    setIsLoading(true);
    setShowConfirmDialog(false);

    const formatTimeForApi = (time: string) => {
      return time ? `${time}:00` : "";
    };

    const updatedFormData = {
      ...formData,
      id: schedule?.id,
      startTime: formatTimeForApi(formData.startTime as string),
      endTime: formatTimeForApi(formData.endTime as string),
      products: JSON.stringify(selectedProducts.map((p) => p.name)),
    };

    const response = await accessURL.post("/schedules/update", updatedFormData);
    if (response.status === 201) {
      toast.success("Schedule updated successfully!");
      setTimeout(() => {
        window.location.reload();
        onClose();
      }, 2000);
    } else {
      toast.error(response.data.message);
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError(name as keyof typeof errors);
  };

  const handleProductsChange = (products: ProductData[]) => {
    setSelectedProducts(products);
    clearError("products");
  };

  return (
    <AnimatePresence>
      {isOpen && schedule && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={onClose}
          />

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                transition={{ duration: 0.2 }}
                className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <form onSubmit={handleSubmit}>
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-bold text-gray-900">
                        Edit Schedule
                      </h2>
                      <button
                        onClick={onClose}
                        type="button"
                        className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                      >
                        <IoClose className="h-5 w-5 text-gray-500" />
                      </button>
                    </div>
                  </div>

                  {/* Form Content */}
                  <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                    <div className="space-y-6">
                      {/* Customer Field (Read-only) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Customer
                        </label>
                        <input
                          type="text"
                          value={formData.customer || ""}
                          readOnly
                          className="mt-1 block w-full px-4 py-2 border rounded-md bg-gray-50"
                        />
                      </div>

                      {/* Date of Visit Field (Read-only) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Date of Visit
                        </label>
                        <input
                          type="text"
                          value={formData.dateOfVisit || ""}
                          readOnly
                          className="mt-1 block w-full px-4 py-2 border rounded-md bg-gray-50"
                        />
                      </div>

                      {/* Objective Field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Objective
                        </label>
                        <input
                          type="text"
                          name="objective"
                          value={formData.objective || ""}
                          onChange={handleInputChange}
                          className={`mt-1 block w-full px-4 py-2 border rounded-md ${
                            errors.objective ? "border-red-500" : ""
                          }`}
                          placeholder="Enter visit objective"
                        />
                        {errors.objective && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.objective}
                          </p>
                        )}
                      </div>

                      {/* Products Selector */}
                      <div>
                        <ProductSelector
                          selectedProducts={selectedProducts}
                          onProductsChange={handleProductsChange}
                        />
                        {errors.products && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.products}
                          </p>
                        )}
                      </div>

                      {/* Time Grid */}
                      <TimeGrid
                        startTime={formData.startTime as string}
                        endTime={formData.endTime as string}
                        onStartTimeChange={(time) =>
                          setFormData((prev) => ({ ...prev, startTime: time }))
                        }
                        onEndTimeChange={(time) =>
                          setFormData((prev) => ({ ...prev, endTime: time }))
                        }
                        errors={errors}
                        clearError={clearError}
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 border-t border-gray-200">
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isLoading || !hasChanges}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        {isLoading ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </div>
                </form>
              </motion.div>
            </div>
          </div>

          {/* Confirmation Modal */}
          <AnimatePresence>
            {showConfirmDialog && (
              <>
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50" />
                <div className="fixed inset-0 z-50 overflow-y-auto">
                  <div className="flex min-h-full items-center justify-center p-4">
                    <motion.div
                      initial={{ scale: 0.95, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.95, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="bg-white rounded-lg shadow-xl w-full max-w-md p-6"
                    >
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Confirm Schedule Update
                      </h3>
                      <p className="text-sm text-gray-500 mb-6">
                        Are you sure you want to update this schedule?
                      </p>
                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={() => setShowConfirmDialog(false)}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleConfirmedSubmit}
                          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {isLoading ? "Updating..." : "Yes, Update Schedule"}
                        </button>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
};

export default EditScheduleModal;
